import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useChat } from '../hooks/useChat';
import '../styles/chatwidget.css';

const renderWithLinks = (text) => {
  const parts = text.split(/(\/[a-z][a-z0-9-]*(?:\/[a-z0-9-]+)*)/g);
  return parts.map((part, i) => {
    if (/^\/[a-z]/.test(part)) {
      return <Link key={`${part}-${i}`} to={part} className="cw-inline-link">{part}</Link>;
    }
    return part;
  });
};

const TypingDots = () => (
  <span className="cw-typing"><span></span><span></span><span></span></span>
);

const ChatMessage = ({ message }) => (
  <div className={`cw-msg ${message.role}`}>
    {message.content ? renderWithLinks(message.content) : <TypingDots />}
  </div>
);

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const { messages, isStreaming, sendMessage } = useChat();
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleSend = () => {
    if (!input.trim() || isStreaming) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-widget">
      {isOpen && (
        <div className="cw-panel" data-testid="chat-panel">
          <div className="cw-header">
            <div className="cw-header-info">
              <span className="cw-header-title">Ask Cal</span>
              <span className="cw-header-sub">GoCalFit assistant</span>
            </div>
            <button className="cw-close" onClick={() => setIsOpen(false)} data-testid="chat-close-btn" aria-label="Close chat">
              <X size={18} />
            </button>
          </div>
          <div className="cw-body" ref={bodyRef} data-testid="chat-messages">
            {messages.map((m) => (
              <ChatMessage key={m.id} message={m} />
            ))}
          </div>
          <div className="cw-input-row">
            <textarea
              ref={inputRef}
              className="cw-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about classes, pricing, ages..."
              rows={1}
              data-testid="chat-input"
            />
            <button
              className="cw-send"
              onClick={handleSend}
              disabled={isStreaming || !input.trim()}
              data-testid="chat-send-btn"
              aria-label="Send message"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
      <button
        className={`cw-bubble ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-bubble-btn"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
};
